<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { getAppConfig } from '@/utils/env';
    import { NCarousel, NCarouselItem } from '@likui628/naive-ui';
    import { useRoute } from 'vue-router';
    import { joinUrl } from '@repo/utils';
    import { type FabricShareInfo, type Relation, useStyle3d, mergeShareFabricInfo, type FabricStore } from '@repo/style3d';
    import axios, { type AxiosResponse } from 'axios';
    import { useParamStore } from '@/store/param';
    import { storeToRefs, useStyle3dStore } from '@repo/stores';
    import { useSelectionStore } from '@repo/stores';
    import { until } from '@vueuse/core';

    const props = defineProps<{ fabrics?: FabricStore[],firstThumb?:string,firstName?:string }>();
    const list = computed(() => {
      let listFabrics = [...(props.fabrics || [])]; 
      if (listFabrics.length === 0) {
        return [];
      }
      if (!props.firstThumb) {
        return listFabrics;
      }
      const emptyItem = { id: '', thumb: props.firstThumb || '', name: props.firstName || '' };
      listFabrics.unshift(emptyItem);
      return listFabrics;
    });

    const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
    const projectRootPath = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
    const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

    const paramStore = useParamStore();
    const { scoId } = storeToRefs(paramStore);

    const style3dStore = useStyle3dStore();
    const { ready: sdkReady } = storeToRefs(style3dStore);

    const { loadFabric, loadSco, clearMaterialSelection, selectMaterial } = useStyle3d();
    const route = useRoute();

    const selectionStore = useSelectionStore();
    const { selectedFabricId } = storeToRefs(selectionStore);

    const rootPath = computed(() => {
      return joinUrl(projectRootPath, route.params.id as string);
    });

    async function loadRelation() {
      const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(rootPath.value, 'relation.json'));
      return jsonResult.data;
    }

    const selectedId = ref<string | undefined>(undefined);

    const onClick = async (item: FabricStore) => {
      selectedId.value = item.id;
      const json = await loadRelation();
      let shapeList = json.shape;

      if (!item.id){ //选中第一个图，款式图，使用原始面料
        const { metadata } = json;
        const model = metadata?.modelType || 'sco';
 
        let url;
        switch (model) {
          case 'glb':
            url = joinUrl(rootPath.value, '/glb/emu.glb');
            break;
          default:
            url = joinUrl(rootPath.value, '/sco/sco.json');
            break;
        }
        await loadSco(url);

        await until(sdkReady).toBe(true);

        let fabricRoot = joinUrl(projectRootPath, scoId.value);
        await loadFabric(shapeList, { fabricRootPath: fabricRoot });

        clearMaterialSelection();
      }
      else{ //选中面料图
        let fabricRoot = joinUrl(fabricRootPath, item.id);
        const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
        const info = infoResult.data;
        shapeList = await mergeShareFabricInfo(json.shape, rootPath.value, info);
        const curID = selectedFabricId.value;
        await loadFabric(shapeList, { fabricRootPath: fabricRoot }, curID?{id:curID}:null);
        if (curID === null){
          clearMaterialSelection();
        }
        else{
          selectMaterial(curID);
        }
      }
    }

    const autoClicked = ref(false);
    watch(list, async (val) => {
      if (!autoClicked.value && val.length >= 1) {
        autoClicked.value = true;
        const first = val[0];
        if (first){
          selectedId.value = first.id;
          //await onClick(first);
        } 
      }
    }, { immediate: true });
</script>

<template>
  <div class="share-list-container">
    <NCarousel slides-per-view="auto" :space-between="20" :loop="false" draggable>
      <!-- 可选：绑定选中态，判断当前item.id是否等于选中的id -->
      <NCarouselItem 
        v-for="(item) in list" 
        :key="item.id" 
        class="carousel-item" 
        :class="{ active: false }"
        @click="onClick(item)"
      >
        <div class="carousel-content">
          <!-- 修复：移除重复的@click，避免事件触发两次 -->
          <img class="carousel-img" :src="item.thumb" @dragstart.prevent />
          <div class="carousel-caption" v-if="item.name">{{ item.name }}</div>
        </div>
      </NCarouselItem>
    </NCarousel>
  </div>
</template>

<style scoped>
.share-list-container {
  padding: 0px 0px 10px;
}

.carousel-item {
  width: 85px !important;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /*  统一对齐方式，避免高度偏差 */
  align-items: center;
  transform: scale(1);
  padding: 4px 2px;
  margin: 0;
  box-sizing: border-box; /*  内边距计入宽度，避免布局偏移 */
  /* ✅ 强制所有卡片固定高度，彻底保证高度一致 */
  height: 160px;
  cursor: pointer;
  transition: all 0.2s ease; /* 选中态过渡动画，更丝滑 */
}

/*  优化选中态：放大+边框高亮，视觉反馈更清晰 */
.carousel-item.active {
  transform: scale(1.1);
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.1);
}

.carousel-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%; /*  撑满父容器高度 */
  padding: 4px 0;
}

/* - 图片容器强制等高：所有图片的外层容器高度一致 */
.carousel-img {
  width: 100%;
  height: 110px; /*  固定图片高度，核心解决高度不一致问题 */
  object-fit: contain; /*  图片居中展示，不拉伸、不变形、留透明边距 */
  object-position: center center; /*  图片绝对居中 */
  border-radius: 4px;
  display: block; /* 消除图片默认的行内空隙 */
}

.carousel-caption {
  margin-top: 5px;
  font-size: 11px;
	font-weight: 400;
	color: #ffffff;
	text-shadow: 0 1px 0 rgba(0,0,0,0.6);
	text-align: center;
  width: 100%;
	line-height: 1.2;
  /* 固定标题高度+溢出省略，避免文字行数不同撑开高度 */
  height: 46px;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>